import moment from 'moment';
import format from 'string-template';

import colors from '../../../styles/colors';
import { DateFormat } from '../../../utils/date';
import { formatPluralDuration, formatPluralUnit } from '../../../utils/durationUnit';
import texts from '../texts.json';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * Everything a commitment sticker shows about a challenge. Built only from data every coach already has,
 * so the sticker works for any coach without coach-specific code.
 *
 * @typedef {Object} ShareCard
 * @property {string} challengeId
 * @property {string} coachId
 * @property {string} coachName
 * @property {string} challengeName
 * @property {string} durationLabel The challenge length, e.g. "6 weeks".
 * @property {number} durationCount The challenge length's number, e.g. 6.
 * @property {string} durationUnitLabel The challenge length's unit, e.g. "weeks".
 * @property {?Date} startDate Local calendar date the challenge starts, null when it has none.
 * @property {string} brandColor The coach's app color, or the Avida default.
 * @property {boolean} isDarkTheme Whether the coach's checkout pages are dark.
 */

/**
 * @param {Object} sources
 * @param {Object} sources.contract The challenge's BASE contract.
 * @param {?Object} sources.coach The challenge's coach.
 * @param {?Object} sources.appCustomization The coach's app customization.
 * @param {boolean} sources.isDarkTheme Whether the coach's checkout pages are dark.
 * @returns {ShareCard}
 */
const buildShareCard = ({
  contract,
  coach,
  appCustomization,
  isDarkTheme,
}) => ({
  challengeId: contract.challengeId,
  coachId: contract.coach,
  coachName: coach?.name || contract.coachName,
  challengeName: contract.name || contract.productName,
  durationLabel: formatPluralDuration(contract.initialTerm, contract.durationUnit),
  durationCount: contract.initialTerm,
  durationUnitLabel: formatPluralUnit(contract.initialTerm, contract.durationUnit),
  // A date-only string, read as the local calendar day it names.
  startDate: contract.startDate ? moment(contract.startDate, DateFormat.DEFAULT_DATE_FORMAT).toDate() : null,
  // The palette does contrast math on the color, which needs a six-digit hex.
  brandColor: HEX_COLOR.test(appCustomization?.primaryColor) ? appCustomization.primaryColor : colors.base.primary,
  isDarkTheme,
});

/**
 * The start date is only worth showing while the challenge is still ahead.
 *
 * @param {ShareCard} card
 * @param {Date} now
 * @returns {?Date} The start date, or null when there is none or the challenge has started.
 */
const getUpcomingStartDate = ({ startDate }, now) => (
  startDate && moment(startDate).isAfter(now, 'day') ? startDate : null
);

/**
 * When the challenge runs, for the client's checklist: the start date and length while it is ahead, and the
 * length alone once it has started.
 *
 * @param {ShareCard} card
 * @param {Date} now
 * @returns {string}
 */
const getScheduleLine = (card, now) => {
  const startDate = getUpcomingStartDate(card, now);
  return startDate
    ? format(texts.app.startsOn, { date: moment(startDate).format('ddd, MMM D'), duration: card.durationLabel })
    : format(texts.app.inProgress, { duration: card.durationLabel });
};

export {
  buildShareCard,
  getUpcomingStartDate,
  getScheduleLine,
};
